import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice, paymentMethodNames, shippingMethodNames } from '../utils/helpers';
import axios from 'axios';
import { CreditCard, Truck, MapPin, FileText, Sparkles } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [calculatingShipping, setCalculatingShipping] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Check if cart contains only services (no physical products)
  const isServiceOnly = cart.products?.length === 0 && cart.services?.length > 0;

  const [formData, setFormData] = useState({
    // Guest email
    guestEmail: '',
    
    // Shipping Address
    shippingName: user?.name || '',
    shippingPhone: user?.phone || '',
    shippingCountry: 'Magyarország',
    shippingCity: '',
    shippingZip: '',
    shippingAddress: '',
    
    // Billing Address
    billingName: '',
    billingCountry: 'Magyarország',
    billingCity: '',
    billingZip: '',
    billingAddress: '',
    billingTaxNumber: '',
    sameAsShipping: true,
    
    // Payment & Shipping
    paymentMethod: 'transfer',
    shippingOptionId: '',
    
    // Notes
    notes: ''
  });

  // Fetch shipping options and payment methods
  useEffect(() => {
    fetchShippingOptions();
    fetchPaymentMethods();
  }, []);

  // Set shipping cost to 0 for service-only orders
  useEffect(() => {
    if (isServiceOnly) {
      setShippingCost(0);
      setFormData(prev => ({ ...prev, shippingOptionId: '' }));
    }
  }, [isServiceOnly]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API_URL}/payment-methods?active_only=true`);
      setPaymentMethods(response.data);
      // Set first active payment method as default
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, paymentMethod: response.data[0].type }));
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    }
  };

  const fetchShippingOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/shipping-options`);
      setShippingOptions(response.data);
      // Only set shipping for physical products
      if (response.data.length > 0 && !isServiceOnly) {
        setFormData(prev => ({ ...prev, shippingOptionId: response.data[0].id }));
        setShippingCost(response.data[0].price);
      }
    } catch (error) {
      console.error('Failed to fetch shipping options:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateShipping = async () => {
    if (!formData.shippingCity || !formData.shippingZip) return;
    
    setCalculatingShipping(true);
    try {
      const response = await axios.post(`${API_URL}/orders/calculate-shipping`, {
        city: formData.shippingCity,
        zip: formData.shippingZip,
        shipping_method: formData.shippingMethod
      });
      setShippingCost(response.data.shipping_cost);
    } catch (error) {
      console.error('Failed to calculate shipping:', error);
    } finally {
      setCalculatingShipping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if ((cart.products?.length === 0 || !cart.products) && (cart.services?.length === 0 || !cart.services)) {
      alert('A kosarad üres!');
      return;
    }
    
    if (!user && !formData.guestEmail) {
      alert('Kérlek add meg az email címedet!');
      return;
    }

    // Only validate shipping for physical products
    if (!isServiceOnly && (!formData.shippingName || !formData.shippingCity || !formData.shippingZip || !formData.shippingAddress)) {
      alert('Kérlek töltsd ki a szállítási címet!');
      return;
    }

    setLoading(true);

    try {
      // Prepare items from products and services
      const productItems = (cart.products || []).map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        type: 'product'
      }));
      
      const serviceItems = (cart.services || []).map(item => ({
        service_id: item.service_id,
        quantity: item.quantity,
        type: 'service',
        service_details: {
          full_name: item.full_name,
          birth_date: item.birth_date,
          questions: item.questions
        }
      }));

      const orderData = {
        items: [...productItems, ...serviceItems],
        shipping_option_id: isServiceOnly ? null : formData.shippingOptionId,
        payment_method: formData.paymentMethod === 'bank_transfer' ? 'transfer' : formData.paymentMethod,  // Map bank_transfer to transfer
        notes: formData.notes,
        guest_email: !user ? formData.guestEmail : null
      };

      // Only include shipping address for physical products
      if (!isServiceOnly) {
        orderData.shipping_address = {
          name: formData.shippingName,
          phone: formData.shippingPhone,
          country: formData.shippingCountry,
          city: formData.shippingCity,
          zip: formData.shippingZip,
          address: formData.shippingAddress
        };
      }

      // Billing address
      orderData.billing_address = formData.sameAsShipping && !isServiceOnly ? {
        name: formData.shippingName,
        phone: formData.shippingPhone,
        country: formData.shippingCountry,
        city: formData.shippingCity,
        zip: formData.shippingZip,
        address: formData.shippingAddress
      } : {
        name: formData.billingName || formData.shippingName,
        phone: formData.billingPhone || formData.shippingPhone || '',
        country: formData.billingCountry,
        city: formData.billingCity,
        zip: formData.billingZip,
        address: formData.billingAddress,
        tax_number: formData.billingTaxNumber
      };

      const sessionId = localStorage.getItem('session_id');
      const response = await axios.post(
        `${API_URL}/orders`,
        orderData,
        { params: { session_id: sessionId } }
      );

      // Clear cart
      await clearCart();

      // Redirect based on payment method
      if (formData.paymentMethod === 'stripe') {
        // Initiate Stripe checkout: amount is computed server-side from the order
        try {
          const checkoutResp = await axios.post(`${API_URL}/payments/checkout/session`, {
            order_id: response.data.id,
            origin_url: window.location.origin
          });
          if (checkoutResp.data?.url) {
            window.location.href = checkoutResp.data.url;
            return;
          }
          throw new Error('No checkout URL received');
        } catch (err) {
          console.error('Stripe session creation failed:', err);
          alert('Hiba történt a kártyás fizetés indításakor. A rendelésed létrejött, kérlek próbáld újra a fizetést.');
          navigate(`/rendeles/${response.data.id}`);
          return;
        }
      } else {
        navigate(`/rendeles/${response.data.id}`);
      }
    } catch (error) {
      console.error('Order creation failed:', error);
      alert('Hiba történt a rendelés során. Kérlek próbáld újra!');
    } finally {
      setLoading(false);
    }
  };

  if (cart.products?.length === 0 && cart.services?.length === 0) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-semibold mb-4">A kosarad üres</h2>
            <p className="text-gray-400 mb-8">Adj hozzá termékeket a pénztár előtt.</p>
            <button onClick={() => navigate('/termekek')} className="btn-magic rounded-lg px-8 py-3">
              Vásárlás kezdése
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="section-title text-4xl md:text-5xl mb-8 text-center">
          <span>Pénztár</span>
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Guest Email */}
              {!user && (
                <div className="glass rounded-xl p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-yellow-200" />
                    Email cím
                  </h2>
                  <input
                    type="email"
                    name="guestEmail"
                    value={formData.guestEmail}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    placeholder="pelda@email.com"
                  />
                </div>
              )}

              {/* Shipping Address - Only for physical products */}
              {!isServiceOnly && (
                <div className="glass rounded-xl p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-yellow-200" />
                    Szállítási cím
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="shippingName"
                    value={formData.shippingName}
                    onChange={handleChange}
                    required
                    placeholder="Teljes név"
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  />
                  <input
                    type="tel"
                    name="shippingPhone"
                    value={formData.shippingPhone}
                    onChange={handleChange}
                    required
                    placeholder="Telefonszám"
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  />
                  <input
                    type="text"
                    name="shippingCity"
                    value={formData.shippingCity}
                    onChange={handleChange}
                    onBlur={calculateShipping}
                    required
                    placeholder="Város"
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  />
                  <input
                    type="text"
                    name="shippingZip"
                    value={formData.shippingZip}
                    onChange={handleChange}
                    onBlur={calculateShipping}
                    required
                    placeholder="Irányítószám"
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  />
                  <input
                    type="text"
                    name="shippingAddress"
                    value={formData.shippingAddress}
                    onChange={handleChange}
                    required
                    placeholder="Utca, házszám"
                    className="md:col-span-2 w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  />
                </div>
              </div>
              )}

              {/* Service-only info */}
              {isServiceOnly && (
                <div className="glass rounded-xl p-6 bg-blue-500/10 border-blue-500/30">
                  <p className="text-blue-200 text-sm flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Ez egy online szolgáltatás rendelés - nincs szükség szállítási címre.
                  </p>
                </div>
              )}

              {/* Billing Address */}
              <div className="glass rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-yellow-200" />
                  Számlázási cím
                </h2>
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="sameAsShipping"
                      checked={formData.sameAsShipping}
                      onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <span className="text-gray-300">Megegyezik a szállítási címmel</span>
                  </label>
                </div>
                {!formData.sameAsShipping && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="billingName"
                      value={formData.billingName}
                      onChange={handleChange}
                      required
                      placeholder="Név"
                      className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    />
                    <input
                      type="text"
                      name="billingTaxNumber"
                      value={formData.billingTaxNumber}
                      onChange={handleChange}
                      placeholder="Adószám (opcionális)"
                      className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    />
                    <input
                      type="text"
                      name="billingCity"
                      value={formData.billingCity}
                      onChange={handleChange}
                      required
                      placeholder="Város"
                      className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    />
                    <input
                      type="text"
                      name="billingZip"
                      value={formData.billingZip}
                      onChange={handleChange}
                      required
                      placeholder="Irányítószám"
                      className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    />
                    <input
                      type="text"
                      name="billingAddress"
                      value={formData.billingAddress}
                      onChange={handleChange}
                      required
                      placeholder="Utca, házszám"
                      className="md:col-span-2 w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    />
                  </div>
                )}
              </div>

              {/* Shipping & Payment Method */}
              <div className="glass rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-yellow-200" />
                  Szállítás és fizetés
                </h2>
                <div className="space-y-4">
                  {/* Shipping Method - Only for physical products */}
                  {!isServiceOnly && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Szállítási mód</label>
                      <select
                        name="shippingOptionId"
                        value={formData.shippingOptionId}
                        onChange={(e) => {
                          handleChange(e);
                          const selected = shippingOptions.find(opt => opt.id === e.target.value);
                          if (selected) {
                            setShippingCost(selected.price);
                          }
                        }}
                        required={!isServiceOnly}
                        className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                      >
                        {shippingOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.name} - {formatPrice(option.price)} ({option.estimated_days})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Payment Method - Dynamic from backend */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Fizetési mód</label>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    >
                      {paymentMethods.length > 0 ? (
                        paymentMethods.map(method => (
                          <option key={method.id} value={method.type}>
                            {method.name}
                          </option>
                        ))
                      ) : (
                        <option value="transfer">Banki átutalás</option>
                      )}
                    </select>
                    {paymentMethods.length > 0 && formData.paymentMethod && (
                      <div className="mt-2 text-xs text-gray-400">
                        {paymentMethods.find(m => m.type === formData.paymentMethod)?.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="glass rounded-xl p-6">
                <label className="block text-sm text-gray-400 mb-2">Megjegyzés (opcionális)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Különleges kérések, üzenetek..."
                  className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
                />
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="glass rounded-xl p-6 sticky top-24">
                <h2 className="text-2xl font-semibold mb-6">Összesítő</h2>
                
                {/* Cart Items */}
                <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                  {/* Products */}
                  {cart.products?.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-gray-300">
                        {item.product_name} × {item.quantity}
                      </span>
                      <span className="font-semibold">{formatPrice(item.subtotal)}</span>
                    </div>
                  ))}
                  {/* Services */}
                  {cart.services?.map((item, index) => (
                    <div key={`service-${index}`} className="flex justify-between text-sm">
                      <span className="text-gray-300">
                        {item.service_name} × {item.quantity}
                      </span>
                      <span className="font-semibold">{formatPrice(item.subtotal)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-4 space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Részösszeg</span>
                    <span className="font-semibold">{formatPrice(cartTotal)}</span>
                  </div>
                  {!isServiceOnly && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Szállítás</span>
                      <span className="font-semibold">
                        {calculatingShipping ? '...' : formatPrice(shippingCost)}
                      </span>
                    </div>
                  )}
                  {isServiceOnly && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Szállítás</span>
                      <span className="text-green-400 font-semibold">Ingyenes</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-white/10 pt-4 mb-6">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Végösszeg</span>
                    <span className="text-yellow-200">{formatPrice(cartTotal + (isServiceOnly ? 0 : shippingCost))}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-magic rounded-lg py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Feldolgozás...' : 'Rendelés leadása'}
                </button>

                {formData.paymentMethod === 'transfer' && (
                  <div className="mt-4 p-3 bg-yellow-200/10 border border-yellow-200/20 rounded-lg">
                    <p className="text-xs text-gray-400">
                      💡 Átutalásos fizetés esetén a rendelés visszaigazolásban megkapod a banki adatokat.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default Checkout;